/**
 * A paged list whose first page a route loader preloads (CR-19).
 *
 *   loader ── preloadRouteQuery(first page) ──────────────► queryRef
 *   hook   ── useRouteQueryRef(first page) ─── same ref ───┘ (its own when no loader)
 *     reader: useReadQuery(ref) under Suspense + error boundary ──report──► rows
 *     page · sort · filter · search change ──► the hook's useQuery (cache-and-network)
 *
 * The reader renders nothing, so the DataGrid, its toolbar and selection never suspend
 * or remount; `loading` drives the grid's existing overlay. Export is unchanged. The reader
 * renders nothing until the list fails; then it renders the Retry notice.
 */
import { type ApolloClient, type TypedDocumentNode, useApolloClient, useQuery } from '@apollo/client';
import { canonicalStringify } from '@apollo/client/utilities';
import { type ReactElement, useMemo, useRef, useState } from 'react';
import { DeferredRetry } from 'src/modules/entities/deferred';
import { useRouteQueryRead } from 'src/routing/useRouteQueryRead';
import { useRouteQueryRef, useRouteQueryRefetch } from 'src/routing/useRouteQueryRef';
import type { StandardPageInput } from 'types/graphql';
import type { PagedGridQueryState } from '../rows/useGridRows';
import type { PaginationWebResult } from './types';
import {
  createExportFunction,
  normalizePageInfo,
  notePageInfoRequest,
  usePagedGridRequest,
} from './useServerEnhancedGridQuery';

/**
 * The variables of a paged list read: one `StandardPageInput`, always sent. Typed as required so a
 * document declaring `$input: StandardPageInput!` (the G1 page roots) is accepted beside the nullable ones.
 */
export type PagedListVariables = { input: StandardPageInput };

/** A list page as the generated types describe it (edges may be null). */
export type PagedListPage<T> = Readonly<{
  edges?: ReadonlyArray<{ node: T } | null> | null;
  pageInfo?: { endCursor?: string | null; hasNextPage?: boolean | null } | null;
  totalCount?: number | null;
}>;

export type PagedListData<TKey extends string, T> = { readonly [K in TKey]: PagedListPage<T> | null | undefined };

/** List reads keep the grid's policy: cached rows paint at once while the server refreshes them. */
export const PAGED_LIST_FETCH_POLICY = 'cache-and-network';

export type PreloadedServerGridQueryArgs<TKey extends string, T> = Readonly<{
  queryKey: TKey;
  query: TypedDocumentNode<PagedListData<TKey, T>, PagedListVariables>;
  gridState: PagedGridQueryState;
  /** What the list holds, plural and lower case: the failure notice reads "Unable to load <label>." */
  label: string;
}>;

export type PreloadedServerGridResult<T> = Omit<PaginationWebResult<T>, 'pageData'> & {
  pageData?: PagedListPage<T>;
  /** Render it once, anywhere beside the grid: it reads the preloaded first page and shows the Retry notice. */
  reader: ReactElement;
};

/**
 * The page the cache holds for exactly `variables`, or `undefined` while it holds none. A page belongs to the
 * request it answers, never to whatever scope was current when it was first rendered: Apollo can hand back the
 * previous request's page for a render, and a late response can land under a newer scope. Labelling pages by
 * first sight hid a scope's cached rows when the user came back to it (QA R2-PO).
 */
function readCachedPage<TKey extends string, T>(
  client: ApolloClient<object>,
  query: TypedDocumentNode<PagedListData<TKey, T>, PagedListVariables>,
  variables: PagedListVariables,
  queryKey: TKey
): PagedListPage<T> | undefined {
  return client.readQuery({ query, variables })?.[queryKey] ?? undefined;
}

export function usePreloadedServerGridQuery<TKey extends string, T>({
  queryKey,
  query,
  gridState,
  label,
}: PreloadedServerGridQueryArgs<TKey, T>): PreloadedServerGridResult<T> {
  const client = useApolloClient();
  const { input } = usePagedGridRequest(gridState);
  const variables = useMemo<PagedListVariables>(() => ({ input }), [input]);
  const variablesKey = canonicalStringify(variables);

  const [firstVariables] = useState(variables);
  const firstPageRef = useRouteQueryRef(query, firstVariables, { fetchPolicy: PAGED_LIST_FETCH_POLICY });
  const refetchFirstPage = useRouteQueryRefetch(query, firstVariables);
  const { read: firstRead, reader } = useRouteQueryRead(firstPageRef);

  // The first change of page, sort, filter or search hands the list to its own query for good.
  const [live, setLive] = useState(false);
  if (!live && variablesKey !== canonicalStringify(firstVariables)) setLive(true);
  const liveQuery = useQuery(query, {
    variables,
    skip: !live,
    fetchPolicy: PAGED_LIST_FETCH_POLICY,
    nextFetchPolicy: 'cache-first',
  });

  const loading = live ? liveQuery.loading : !firstRead.settled;
  const error = live ? liveQuery.error : firstRead.error;
  const firstPageFailed = !live && firstRead.error !== undefined;

  // The live query and the first-page read only re-render the hook: the rows are the page the cache holds for THIS request.
  const cachedPage = readCachedPage(client, query, variables, queryKey);
  // Same-variables refetch keeps the last complete page; an error drops it.
  const lastPageRef = useRef<{ variablesKey: string; page: PagedListPage<T> }>();
  if (cachedPage !== undefined) lastPageRef.current = { variablesKey, page: cachedPage };
  const keptPage =
    error === undefined && lastPageRef.current?.variablesKey === variablesKey ? lastPageRef.current.page : undefined;
  const page = cachedPage ?? keptPage;
  const isCurrentScopeResponse = page !== undefined;
  const requestSucceeded = !loading && !error;
  const liveRefetch = liveQuery.refetch;

  const result = useMemo(() => {
    const current = isCurrentScopeResponse ? page : undefined;
    const pageInfo = current && requestSucceeded ? normalizePageInfo(current.pageInfo) : undefined;
    if (pageInfo) notePageInfoRequest(pageInfo, input);
    return {
      rows: current?.edges?.flatMap((edge) => (edge ? [edge.node] : [])) ?? [],
      totalCount: current?.totalCount ?? null,
      pageData: current,
      pageInfo,
      loading: loading && !isCurrentScopeResponse,
      error,
      fetchAllForExport: createExportFunction<T>({ client, query, queryKey, variables, input }),
      refetch: () => {
        // The preloaded ref's boundary stays on its error; the list's own query reads the page again.
        if (firstPageFailed) {
          setLive(true);
          return;
        }
        void (live ? liveRefetch(variables) : refetchFirstPage()).catch(() => {});
      },
    };
  }, [
    isCurrentScopeResponse,
    page,
    requestSucceeded,
    input,
    loading,
    error,
    client,
    query,
    queryKey,
    variables,
    live,
    firstPageFailed,
    liveRefetch,
    refetchFirstPage,
  ]);

  const retry =
    error === undefined ? null : (
      <DeferredRetry message={`Unable to load ${label}.`} actionLabel={`Retry ${label}`} onRetry={result.refetch} />
    );

  return {
    ...result,
    reader: (
      <>
        {reader}
        {retry}
      </>
    ),
  };
}
